/** Verträge im Creator-Portal: nur über die Sicht talent_contracts (ohne verborgene Anlage, ohne Kontaktdaten der Marke). */
import type { ContractRole, CustomVariableDef } from "@talentkontor/domain";
import type { Api } from "../../../data/api";
import type { CreatorScope } from "../data";
import { pngBlobToDataUrl, withDarkInk } from "../../agentur/vertraege/tinte";

export interface TalentSigner {
  role: ContractRole;
  slot: number;
  capacity: "party" | "legal_guardian";
  name: string;
  signed: boolean;
  signed_at: string | null;
  signing_name: string | null;
  signing_position: string | null;
  signing_authority: string | null;
  signature_data: string | null;
  /** auf Papier unterschrieben (das eingescannte Papier ist der Beleg, keine Lesebestätigung) */
  paper?: boolean;
  declined: boolean;
}
export interface TalentContract {
  id: string;
  org_id: string;
  deal_id: string | null;
  brand_id: string | null;
  contract_number: number | null;
  contract_number_label: string | null;
  title: string;
  language: "de" | "en";
  status: "sent" | "partially_signed" | "signed" | "void";
  rendered_html: string | null;
  rendered_annex_html: string | null;
  required_signers: ContractRole[];
  origin: "portal" | "pdf_upload";
  sent_at: string | null;
  completed_at: string | null;
  voided_at: string | null;
  void_reason: string | null;
  void_kind: "withdrawn" | "error" | "mutual" | "superseded" | null;
  declined_at: string | null;
  terminated_on: string | null;
  starts_on: string | null;
  ends_on: string | null;
  signed_pdf_path: string | null;
  signed_pdf_path_talent: string | null;
  /** Aufhebungsvertrag einer einvernehmlichen Aufhebung (0086, CREATOR-R2-2); sonst leer */
  void_document_path?: string | null;
  created_at: string;
  fillable_defs: CustomVariableDef[];
  filled_values: Record<string, string>;
  locked: boolean;
  signers: TalentSigner[];
  /** Der Creator unterschreibt selbst (nicht nur die gesetzliche Vertretung) */
  talent_signs: boolean;
  guardian_signs: boolean;
  deal_cancelled: boolean;
  /** 0071: Dokument der Marke (PDF) mit Prüfwert; wann der Creator das Lesen bestätigt hat */
  document_kind?: "template" | "brand_pdf";
  source_pdf_path?: string | null;
  source_pdf_sha256?: string | null;
  source_pdf_name?: string | null;
  source_pdf_size?: number | null;
  document_read_at?: string | null;
  /** 0093: automatische Verlängerung (ohne sie endet ein befristeter Vertrag am Ende von selbst, VERTRAEGE-R3-01) */
  auto_renew?: boolean | null;
  /** 0093 (CREATOR-R3-2): minderjährig ohne gesetzliche Vertretung, Unterschreiben gesperrt */
  guardian_required?: boolean;
}

const LIST = "id, org_id, deal_id, brand_id, contract_number, contract_number_label, title, language, status, required_signers, origin, sent_at, completed_at, voided_at, void_reason, void_kind, declined_at, terminated_on, starts_on, ends_on, signed_pdf_path, signed_pdf_path_talent, created_at, fillable_defs, filled_values, locked, signers, talent_signs, guardian_signs, deal_cancelled, document_kind, auto_renew, guardian_required";

export async function loadMeineVertraege(api: Api, s: CreatorScope): Promise<TalentContract[]> {
  return api.select<TalentContract>("talent_contracts", { columns: LIST, filters: [["org_id", "eq", s.orgId], ["talent_id", "eq", s.talentId]], order: [["created_at", "desc"]] });
}

export async function loadMeinVertrag(api: Api, s: CreatorScope, id: string): Promise<TalentContract | null> {
  return (await api.select<TalentContract>("talent_contracts", { filters: [["id", "eq", id], ["org_id", "eq", s.orgId], ["talent_id", "eq", s.talentId]] }))[0] ?? null;
}

export type MeinStand = "sign" | "wait_fill" | "signed" | "declined" | "stopped" | "paused" | "readonly" | "guardian" | "minor" | "info" | "void" | "done" | "ended";

/** Tag, an dem ein unterschriebener Vertrag endete (gekündigt oder befristet ohne Verlängerung), wenn er schon vorbei ist. */
export function vertragsEnde(c: Pick<TalentContract, "status" | "terminated_on" | "ends_on" | "auto_renew">, today?: string | null): string | null {
  if (c.status !== "signed" || !today) return null;
  const end = c.terminated_on ?? (c.ends_on && !c.auto_renew ? c.ends_on : null);
  return end && end.slice(0, 10) < today.slice(0, 10) ? end.slice(0, 10) : null;
}

/** Was der Creator bei diesem Vertrag tun kann (Befund 5 und 12); mit `today` sind vorbei gegangene Verträge „beendet“; in der
 * Nur-Lesen-Phase der Agentur ist ein offener Vertrag angehalten (VERTRAEGE-R3-09). */
export function meinStand(c: TalentContract, today?: string | null, nurLesen = false): MeinStand {
  if (c.status === "void") return "void";
  if (c.status === "signed") return vertragsEnde(c, today) ? "ended" : "done";
  const me = c.signers.find((x) => x.role === "talent" && x.capacity === "party");
  if (me?.declined) return "declined";
  if (c.declined_at) return "stopped";
  if (!c.talent_signs) return c.guardian_signs ? "guardian" : "info";
  if (me?.signed) return "signed";
  if (c.deal_cancelled) return "paused";
  // CREATOR-R3-2: minderjährig ohne gesetzliche Vertretung; die Agentur schickt eine neue Fassung mit den Eltern
  if (c.guardian_required) return "minor";
  return nurLesen ? "readonly" : "sign";
}

export const STAND_PILL: Record<MeinStand, { label: string; en: string; tone: string; symbol: string }> = {
  sign: { label: "Bitte unterschreiben", en: "Please sign", tone: "warning", symbol: "◷" },
  wait_fill: { label: "Wartet auf Angaben", en: "Waiting for details", tone: "info", symbol: "◇" },
  signed: { label: "Du hast unterschrieben", en: "You have signed", tone: "success", symbol: "✓" },
  declined: { label: "Abgelehnt", en: "Declined", tone: "error", symbol: "!" },
  stopped: { label: "Angehalten", en: "On hold", tone: "neutral", symbol: "○" },
  paused: { label: "Pausiert", en: "Paused", tone: "neutral", symbol: "○" },
  readonly: { label: "Angehalten (Agentur nur lesend)", en: "On hold (agency read-only)", tone: "neutral", symbol: "○" },
  guardian: { label: "Unterschrift durch deine gesetzliche Vertretung", en: "Signed by your legal guardian", tone: "info", symbol: "◇" },
  minor: { label: "Deine Eltern müssen mit unterschreiben", en: "Your parents must also sign", tone: "warning", symbol: "!" },
  info: { label: "Zur Kenntnis", en: "For your information", tone: "neutral", symbol: "○" },
  void: { label: "Aufgehoben", en: "Voided", tone: "neutral", symbol: "○" },
  done: { label: "Vollständig unterschrieben", en: "Fully signed", tone: "success", symbol: "✓" },
  ended: { label: "Beendet", en: "Ended", tone: "neutral", symbol: "○" },
};

/** Stand als Pille in der Sprache des Portals (Befund 2). */
export function standPill(st: MeinStand, lang: "de" | "en" = "de"): { label: string; tone: string; symbol: string } {
  const p = STAND_PILL[st];
  return { label: lang === "en" ? p.en : p.label, tone: p.tone, symbol: p.symbol };
}

/** Überschrift des Papiers je Stand (Befund 24: ein aufgehobener Vertrag ist kein „Vertrag zur Unterschrift“). */
export function paperTitle(st: MeinStand, lang: "de" | "en" = "de"): string {
  const en = lang === "en";
  switch (st) {
    case "done": case "ended": return en ? "Signed contract" : "Unterschriebener Vertrag";
    case "void": return en ? "Voided contract" : "Aufgehobener Vertrag";
    case "info": case "guardian": return en ? "Contract for your information" : "Vertrag zur Kenntnis";
    case "stopped": case "paused": case "readonly": case "minor": return en ? "Contract on hold" : "Angehaltener Vertrag";
    default: return en ? "Contract to sign" : "Vertrag zur Unterschrift";
  }
}

/**
 * Unterschriftsbild aus dem Profil als PNG-data-URL (höchstens 200 KB), sonst null. Helle Tinte älterer, im Dunkelmodus
 * gezeichneter Unterschriften wird dunkel umgefärbt, sonst stünde sie unsichtbar auf dem weißen Vertrag (SICHT-CREATOR-23).
 */
export async function blobToPngDataUrl(blob: Blob, ink: (url: string) => Promise<string> = withDarkInk): Promise<string | null> {
  if (blob.type !== "image/png" || blob.size > 140_000) return null;
  const url = await ink(await pngBlobToDataUrl(blob));
  return url.length <= 200_000 ? url : null;
}
